import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { CompatibilityValueTypes } from 'src/app/core/common/enums/select-solution.enum';
import {
  DiagnosisSolutionCompatibility,
  DiagnosisSolutionCreateDto,
} from 'src/app/core/common/models/workflow/diagnosis-solution';
import { StoreService } from 'src/app/core/services/global-store/store.service';
import { LoadingDirective } from '../../../../../core/common/directives/loading/loading.directive';
import { AvailableSolutionStore } from './available-solutions.store';

@Component({
  selector: 'app-available-solutions',
  imports: [
    ButtonModule,
    NgTemplateOutlet,
    CheckboxModule,
    FormsModule,
    NgClass,
    TooltipModule,
    NgStyle,
    LoadingDirective,
  ],
  templateUrl: './available-solutions.component.html',
  styleUrl: './available-solutions.component.scss',
})
export class AvailableSolutionsComponent implements OnInit {
  readonly store = inject(AvailableSolutionStore);
  readonly #storeService = inject(StoreService);

  @Input() diagnosisSessionId: string = '';

  availableSolutions: DiagnosisSolutionCompatibility[] = [];
  compatibilityValueTypes = CompatibilityValueTypes;

  ngOnInit(): void {
    this.store.loadAvailableSolutions(this.diagnosisSessionId);
  }

  unselectAllSolutions(): void {
    this.store.unselectAllSolutions(this.diagnosisSessionId);
    this.refreshSolutionSelectionOnUI();
  }

  selectOneSolution(solutionId: string): void {
    const selectedSolution: DiagnosisSolutionCreateDto = {
      diagnosisId: this.diagnosisSessionId,
      solutionId,
    };
    this.store.selectOneSolution(selectedSolution);
  }

  unselectOneSolution(solutionId: string): void {
    const selectedSolution: DiagnosisSolutionCreateDto = {
      diagnosisId: this.diagnosisSessionId,
      solutionId,
    };
    this.store.unselectOneSolution(selectedSolution);
    this.store.isSolutionSelected(this.store.filteredSolutionsByCategory());
  }

  refreshSolutionSelectionOnUI(): void {
    this.store.filteredSolutionsByCategory().map((item) => ({
      ...item,
      compatibilities: item.compatibilities.map((compatibility) => ({
        ...compatibility,
        isChecked: false,
      })),
    }));
    this.#storeService.dispatchSelectedSolution(false);
  }

  onCheckboxChange(compatibility: DiagnosisSolutionCompatibility): void {
    if (compatibility.isChecked)
      this.unselectOneSolution(compatibility.availableSolution.id);
    else this.selectOneSolution(compatibility.availableSolution.id);
  }

  isNotCompatible(compatibility: DiagnosisSolutionCompatibility): boolean {
    if (compatibility.compatibilityValue)
      return (
        compatibility.compatibilityValue.name.toLowerCase() ===
        CompatibilityValueTypes.NotCompatible
      );
    return false;
  }

  getSolutionIcon(compatibility: DiagnosisSolutionCompatibility): string {
    if (compatibility.compatibilityValue) {
      const blinkIcon = compatibility.message ? 'blink_me' : '';

      switch (compatibility.compatibilityValue.name.toLocaleLowerCase()) {
        case CompatibilityValueTypes.AddsSynergy:
          return `fa-solid fa-circle-check checked-icon ${blinkIcon}`;
        case CompatibilityValueTypes.NotIdeal:
          return `fa-solid fa-triangle-exclamation warning-icon ${blinkIcon}`;
        case CompatibilityValueTypes.Compatible:
          return `fa-solid fa-circle-info info-icon ${blinkIcon}`;
      }
    }
    return '';
  }

  formatSolutionMessages(message: string): string {
    if (message !== '') {
      const compatibilityMessages = message.split('<>');
      const compatibilityMessage = compatibilityMessages.map((element) => {
        return this.formatOneMessage(element);
      });
      return compatibilityMessage.join('\n');
    }

    return '';
  }

  formatOneMessage(message: string) {
    if (message.trim() === '') return '';

    const messageParts = message.split('||');
    // Check if messageParts has at least two elements
    if (messageParts.length < 2) {
      return message; // Return the original message if it doesn't match the expected format
    }
    const subParts = messageParts[1].split('>>');
    const relationship = subParts[0];
    const content = subParts[1];

    return `${relationship.replace('->', '⇄')}: ${content}`;
  }
}
